import "./campaign-segment-selector.css";
import useAppContext from "../../hooks/useAppContext";
import Checkbox from "../forms/checkbox/Checkbox";
import React, {useEffect, useMemo, useState} from "react";
import {getShopifyAdminUrl} from "../../utils/getShopifyAdminUrl";
import useUserContext from "../../hooks/useUserContext";
import {Button, Tooltip} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {useSteps} from "../../hooks/useSteps";
import {updateCampaignSegments} from "../../api/updateCampaignSegments";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import SegmentsIllustration from "../../assets/illustrations/SegmentsIllustration";
import ConsentPolicySelector from "../shopConsentPolicy/ConsentPolicySelector";
import SegmentCheckbox from "./SegmentCheckbox";
import {getCampaignMembersCount} from "../../api/getCampaignMembersCount";

interface CampaignSegmentSelectorProps {
    campaignId: number;
}

const CampaignSegmentSelector = ({ campaignId }: CampaignSegmentSelectorProps) => {
    const { goBack, goNext } = useSteps();
    const { data: { shop, segments, campaigns }, actions: { loadCampaigns }} = useAppContext();
    const { selectedDomain } = useUserContext();
    const shopifyAdminUrl = selectedDomain ? getShopifyAdminUrl(selectedDomain) : '';
    const [initialSegmentIds, setInitialSegmentIds] = useState<number[]>([]);
    const [disabledReason, setDisabledReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
    const { showToast } = useToast();
    const [consentRule, setConsentRule] = useState<number | null>(null);
    const campaign = useMemo(() => campaigns?.data?.find((campaign: any) => campaign.id === campaignId), [campaigns, campaignId]);
    const isDirty = useMemo(() => {
        return selectedSegmentIds?.join(',') !== initialSegmentIds?.join(',') || consentRule !== campaign?.consentRule;
    }, [selectedSegmentIds, initialSegmentIds, consentRule]);
    const [totalEligibleMembersWithPhone, setTotalEligibleMembersWithPhone] = useState<number>(0);

    const updateMembersCount = async () => {
        if(!selectedDomain || !selectedSegmentIds.length) return;
        const newCount = await getCampaignMembersCount(selectedDomain, campaignId, selectedSegmentIds, (consentRule || 0));
        if(newCount.data) {
            setTotalEligibleMembersWithPhone(newCount.data?.membersCount || 0);
            setDisabledReason('');
        } else {
            setTotalEligibleMembersWithPhone(0);
        }
    }

    const onUpdateSegments = async () => {
        if(isDirty) {
            if (selectedDomain && campaignId && consentRule !== undefined && consentRule !== null) {
                setIsLoading(true);
                const response = await updateCampaignSegments(selectedDomain, campaignId, selectedSegmentIds, consentRule);
                if (response?.data?.id) {
                    setInitialSegmentIds(selectedSegmentIds);
                    loadCampaigns();
                    goNext();
                    setIsLoading(false);
                } else {
                    showToast({skin: ToastSkin.error, text: 'Failed to update audience for this campaign'});
                    setIsLoading(false);
                }
            }
        } else {
            goNext();
        }
    }

    const updateSelectedSegments = (segmentId: number, newState: boolean) => {
        if(newState && !selectedSegmentIds?.includes(segmentId)) {
            setSelectedSegmentIds([...selectedSegmentIds, segmentId]);
        } else if(!newState && selectedSegmentIds?.includes(segmentId)) {
            setSelectedSegmentIds(selectedSegmentIds.filter(selectedSegmentId => selectedSegmentId !== segmentId))
        }
    }

    useEffect(() => {
        if(!selectedSegmentIds?.length) {
            setDisabledReason('Select at least one segment');
            setTotalEligibleMembersWithPhone(0);
        } else {
            updateMembersCount();
        }
    }, [selectedSegmentIds, consentRule]);

    useEffect(() => {
        if(campaignId) {
            const campaignSegments = campaign?.segments;
            const campaignSegmentIds = campaignSegments?.map((segment: any) => segment.id);
            if(campaignSegments) {
                setInitialSegmentIds(campaignSegmentIds);
                setSelectedSegmentIds(campaignSegmentIds);
            }
        }
    }, [campaigns, campaignId]);

    useEffect(() => {
        if(campaign?.consentRule !== undefined && campaign?.consentRule !== null) {
            setConsentRule(campaign.consentRule);
        } else {
            setConsentRule(shop.data.consentRule);
        }
    }, [shop, campaign]);

    const segmentMembersCountFieldName = consentRule === 0 ? 'clientsCountEstimation' : consentRule === 1 ? 'clientsCountEstimationEmail' : 'clientsCountEstimationSms';
    const modal = document.getElementById('new-campaign-modal');
    const modalHeight = modal?.clientHeight;
    const segmentsHeight = (modalHeight || 0) - 225 - 138;

    return <div className="campaign-segment-selector-container">
        <div className="top">
            <div className="title">Choose Your Audience {totalEligibleMembersWithPhone ? <span>({totalEligibleMembersWithPhone} unique members with phone numbers)</span> : null}</div>
            <div className="sub-title">Select the segments you want to reach with this campaign.</div>
            <div className="sub-title">
                <div>Consent rule for this campaign:</div>
                <ConsentPolicySelector consentPolicy={consentRule} setConsentPolicy={() => null} onSelect={(newVal) => setConsentRule(newVal)} size="small" className="dropdown-text" type="campaign" campaignId={campaignId} />
            </div>
            <div className="lists">
                <div className="left">
                    <div className="segments-list" style={{ maxHeight: `${segmentsHeight}px` }}>
                        {segments?.data?.sort((a: any, b: any) => b[segmentMembersCountFieldName] - a[segmentMembersCountFieldName]).map((segment: any) =>
                            <SegmentCheckbox key={`segment-checkbox-${segment.id}`} segment={segment} consentRule={consentRule!} isSelected={selectedSegmentIds?.includes(segment.id)} toggleSegment={updateSelectedSegments}  />
                        )}
                    </div>
                    <div className="actions">
                        <Button type="default" size="large" className="back-step-button" onClick={goBack} icon={<LeftOutlined />}>{'Back'}</Button>
                        <Tooltip title={disabledReason}>
                            <Button loading={isLoading} type="primary" size="large" className="next-step-button" onClick={onUpdateSegments} iconPosition="end" icon={<RightOutlined />} disabled={!selectedSegmentIds?.length}>Next Step</Button>
                        </Tooltip>
                    </div>
                </div>
                <div className="right">
                    <div className="segments-image">
                        <SegmentsIllustration />
                    </div>
                </div>
            </div>
        </div>
    </div>
}

export default CampaignSegmentSelector;